import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, apiGet } from "@/contexts/AuthContext";
import { BookOpen, Users, FolderOpen, ArrowRight, LogOut, LayoutDashboard, MessageCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Nav() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  return (
    <nav className="glass-nav" data-testid="dashboard-nav">
      <div className="max-w-6xl mx-auto flex items-center justify-between px-6 sm:px-12 py-4">
        <span className="text-lg" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#2D3A31', fontWeight: 600 }}>sober-curious.com</span>
        <div className="flex items-center gap-1 sm:gap-3">
          <Link to="/dashboard" className="earthy-btn earthy-btn-primary text-xs py-2 px-3 flex items-center gap-1.5" data-testid="nav-dashboard">
            <LayoutDashboard size={15} strokeWidth={1.5} /> <span className="hidden sm:inline">Dashboard</span>
          </Link>
          <Link to="/program" className="earthy-btn text-xs py-2 px-3 flex items-center gap-1.5" style={{ color: '#5B665E' }} data-testid="nav-program">
            <BookOpen size={15} strokeWidth={1.5} /> <span className="hidden sm:inline">Program</span>
          </Link>
          <Link to="/community" className="earthy-btn text-xs py-2 px-3 flex items-center gap-1.5" style={{ color: '#5B665E' }} data-testid="nav-community">
            <MessageCircle size={15} strokeWidth={1.5} /> <span className="hidden sm:inline">Community</span>
          </Link>
          <Link to="/resources" className="earthy-btn text-xs py-2 px-3 flex items-center gap-1.5" style={{ color: '#5B665E' }} data-testid="nav-resources">
            <FolderOpen size={15} strokeWidth={1.5} /> <span className="hidden sm:inline">Resources</span>
          </Link>
          <button onClick={async () => { await logout(); navigate("/"); }} className="earthy-btn text-xs py-2 px-3" style={{ color: '#964545' }} data-testid="nav-logout">
            <LogOut size={15} strokeWidth={1.5} />
          </button>
        </div>
      </div>
    </nav>
  );
}

export { Nav };

export default function Dashboard() {
  const { user } = useAuth();
  const [progress, setProgress] = useState(null);
  const [weeks, setWeeks] = useState([]);

  useEffect(() => {
    Promise.all([
      apiGet(`${API}/progress`),
      apiGet(`${API}/program/weeks`)
    ]).then(([p, w]) => { setProgress(p.data); setWeeks(w.data); }).catch(console.error);
  }, []);

  const pct = progress?.percent_complete || 0;
  const currentWeek = progress?.current_week || 1;

  return (
    <div className="min-h-screen" style={{ background: '#F7F5F0' }}>
      <Nav />
      <main className="px-6 sm:px-12 py-10 max-w-6xl mx-auto">
        {/* Welcome + Progress */}
        <div className="grid lg:grid-cols-3 gap-8 mb-10">
          <div className="lg:col-span-2 earthy-card p-0 overflow-hidden fade-in" data-testid="welcome-card">
            <div className="flex flex-col sm:flex-row">
              <div className="p-8 sm:p-10 flex-1">
                <p className="overline mb-4">Week {currentWeek} of 4</p>
                <h1 className="text-3xl sm:text-4xl mb-4" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#2D3A31' }}>
                  Welcome back, {user?.name || 'there'}
                </h1>
                <p className="text-sm mb-8" style={{ color: '#5B665E' }}>
                  {pct === 0 ? "Ready to start understanding that squirrelly nervous system?" 
                    : pct === 100 ? "You've completed the program. Your nervous system thanks you."
                    : `You're ${pct}% through. Keep going \u2014 your nervous system is learning new tricks.`}
                </p>
                <Link to={`/program/${currentWeek}`} className="earthy-btn earthy-btn-primary text-sm inline-flex items-center gap-2" data-testid="continue-lesson-btn">
                  {pct === 0 ? "Start Week 1" : "Continue Learning"} <ArrowRight size={16} strokeWidth={1.5} />
                </Link>
              </div>
              <div className="w-full sm:w-56 h-48 sm:h-auto flex-shrink-0">
                <img src="https://images.unsplash.com/photo-1759596436849-33b6e33e8ea4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHw0fHxjYWxtJTIwcGVhY2VmdWwlMjBtb3JuaW5nJTIwbGlnaHQlMjBuYXR1cmV8ZW58MHx8fHwxNzgwOTQ2NjAzfDA&ixlib=rb-4.1.0&q=85" alt="Golden light" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>

          <div className="earthy-card fade-in stagger-2" data-testid="progress-card">
            <h2 className="text-xl mb-5" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#2D3A31' }}>Your Progress</h2>
            <div className="earthy-progress mb-3">
              <div className="earthy-progress-fill" style={{ width: `${Math.max(pct, 3)}%` }}></div>
            </div>
            <p className="text-sm font-medium mb-6" style={{ color: '#4A5D4E' }}>{pct}% Complete</p>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between" style={{ color: '#5B665E' }}><span>Weeks completed</span><span className="font-medium" style={{ color: '#2D3A31' }}>{progress?.completed_weeks?.length || 0}/4</span></div>
              <div className="flex justify-between" style={{ color: '#5B665E' }}><span>Journal entries</span><span className="font-medium" style={{ color: '#2D3A31' }}>{progress?.journal_entries || 0}</span></div>
              <div className="flex justify-between" style={{ color: '#5B665E' }}><span>Community posts</span><span className="font-medium" style={{ color: '#2D3A31' }}>{progress?.community_posts || 0}</span></div>
            </div>
          </div>
        </div>

        {/* Week Timeline */}
        <div className="mb-10 fade-in stagger-3">
          <h2 className="text-2xl mb-6" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#2D3A31' }}>Program Timeline</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {weeks.map((w) => (
              <Link
                key={w.week_num}
                to={w.status !== 'locked' ? `/program/${w.week_num}` : '#'}
                className={`earthy-card p-5 text-center ${w.status === 'locked' ? 'opacity-40 cursor-not-allowed' : ''}`}
                style={w.status === 'completed' ? { borderColor: '#4A5D4E' } : {}}
                data-testid={`week-card-${w.week_num}`}
                onClick={(e) => w.status === 'locked' && e.preventDefault()}
              >
                <div className="w-10 h-10 rounded-full mx-auto mb-3 flex items-center justify-center text-sm font-medium"
                  style={{ background: w.status === 'completed' ? '#4A5D4E' : 'rgba(74,93,78,0.1)', color: w.status === 'completed' ? '#F7F5F0' : '#4A5D4E' }}>
                  {w.week_num}
                </div>
                <p className="text-xs font-medium leading-tight mb-2" style={{ color: '#2D3A31' }}>{w.title}</p>
                <span className={`earthy-badge inline-block text-[10px] ${w.status === 'completed' ? 'badge-completed' : w.status === 'active' ? 'badge-active' : 'badge-locked'}`}>
                  {w.status}
                </span>
              </Link>
            ))}
          </div>
        </div>

        {/* Quick Links */}
        <div className="grid sm:grid-cols-3 gap-6 fade-in stagger-4">
          <Link to="/community" className="earthy-card flex items-center gap-4" data-testid="quick-community">
            <Users size={24} strokeWidth={1.5} style={{ color: '#4A5D4E' }} />
            <div><h3 className="font-medium text-base" style={{ color: '#2D3A31' }}>Community</h3><p className="text-sm" style={{ color: '#5B665E' }}>Chat with your cohort</p></div>
          </Link>
          <Link to="/resources" className="earthy-card flex items-center gap-4" data-testid="quick-resources">
            <FolderOpen size={24} strokeWidth={1.5} style={{ color: '#A67C52' }} />
            <div><h3 className="font-medium text-base" style={{ color: '#2D3A31' }}>Resources</h3><p className="text-sm" style={{ color: '#5B665E' }}>Worksheets & guides</p></div>
          </Link>
          <Link to="/program" className="earthy-card flex items-center gap-4" data-testid="quick-program">
            <BookOpen size={24} strokeWidth={1.5} style={{ color: '#4A5D4E' }} />
            <div><h3 className="font-medium text-base" style={{ color: '#2D3A31' }}>Full Program</h3><p className="text-sm" style={{ color: '#5B665E' }}>See all 4 weeks</p></div>
          </Link>
        </div>
      </main>
    </div>
  );
}
